import { Typography } from '@uigovpe/components';
import { TimelineEvent } from '@/shared/types/certificate-request';
import { formatDateTime } from '@/shared/utils/formatting';

/**
 * O cidadão só precisa saber em qual das três etapas do fluxo a solicitação
 * está. Eventos internos do backoffice — como "Analisada pela equipe"
 * (REQUEST_VIEWED), disparado toda vez que um analista abre o detalhe —
 * não dizem nada útil pro cidadão e só poluíam a linha do tempo (pedido
 * explícito: só "Enviada", "Em andamento" e "Emitida"). Indeferimento e
 * pendência de resposta continuam visíveis, mas na tag de status e na
 * mensagem acima da página, não aqui.
 */
const MILESTONE_LABELS = {
  submitted: 'Enviada',
  inProgress: 'Em andamento',
  completed: 'Emitida',
} as const;

type Milestone = keyof typeof MILESTONE_LABELS;

function milestoneFor(event: TimelineEvent): Milestone | null {
  if (event.action === 'REQUEST_SUBMITTED') return 'submitted';
  if (event.action === 'CERTIFICATE_EMITTED' || event.newStatus === 'COMPLETED') return 'completed';
  if (event.action === 'STATUS_CHANGED' && event.newStatus === 'IN_PROGRESS') return 'inProgress';
  return null;
}

export function RequestTimeline({ events }: { events: TimelineEvent[] }) {
  const milestones = events
    .map((event) => ({ event, milestone: milestoneFor(event) }))
    .filter(
      (item): item is { event: TimelineEvent; milestone: Milestone } => item.milestone !== null,
    );

  if (!milestones.length) return null;

  return (
    <ol className="cd-timeline">
      {milestones.map(({ event, milestone }, index) => (
        <li key={`${milestone}-${index}`} className="cd-timeline__item">
          <span className="cd-timeline__marker" aria-hidden />
          <div className="cd-timeline__content">
            <Typography variant="span" size="default" fontWeight="medium">
              {MILESTONE_LABELS[milestone]}
            </Typography>
            <Typography variant="p" size="sm" className="cd-timeline__when">
              {formatDateTime(event.occurredAt)}
            </Typography>
          </div>
        </li>
      ))}
    </ol>
  );
}
